import { throwError as observableThrowError, Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Injectable, NgZone, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { SessionStorageService } from './session-storage.service';
import { ThemeService } from './theme.service';
import { MatDialog } from '@angular/material/dialog';
import { GlobalsService } from '../services/globals.service';
import { EnvironmentService } from '../services/environment.service';
import jwt_decode from 'jwt-decode';
import { ErrorReportingService } from './error-reporting.service';
import {
  ApiResponse,
  DynamicResponse,
  GridResults,
  Notes,
  PostbackResponse,
  ResultString,
  StringResult,
} from '../models/general-models';
import { Global, GroupProfile } from '../models/global.model';
import { AnnuityItem, CarrierGroup } from '../models/annuity-models';
import {
  PershingSetting,
  QueryQuestionnaire,
  Questionnaire,
  Unified,
} from '../models/unified.model';
import { inactiveUsersApps } from '../lib/bypassApps';
import { Login, RepData, UnifiedPrintParameters } from '../models/admin-models';
import { ProcessProductsResponse } from '../models/product-models';
import { ClientSummary } from '../models/suggestion-engine-models';

@Injectable({ providedIn: 'root' })
export class RightBridgeApiService {
  private http = inject(HttpClient);
  private ss = inject(SessionStorageService);
  private router = inject(Router);
  private dialogRef = inject(MatDialog);
  private globalsService = inject(GlobalsService);
  private envSvc = inject(EnvironmentService);
  private zone = inject(NgZone);
  private errSvc = inject(ErrorReportingService);
  private themSvc = inject(ThemeService);

  private environment;
  private sessionId: string = this.ss.get('session');
  private token: string = this.ss.get('token');
  public baseUrl: string;
  public f5500BaseUrl: string;
  private firm: string;
  private app: string;
  private org: string;
  private key: any = 'MetallicSwamp';
  private tabulaRecta = {
    a: 'abcdefghijklmnopqrstuvwxyz',
    b: 'bcdefghijklmnopqrstuvwxyza',
    c: 'cdefghijklmnopqrstuvwxyzab',
    d: 'defghijklmnopqrstuvwxyzabc',
    e: 'efghijklmnopqrstuvwxyzabcd',
    f: 'fghijklmnopqrstuvwxyzabcde',
    g: 'ghijklmnopqrstuvwxyzabcdef',
    h: 'hijklmnopqrstuvwxyzabcdefg',
    i: 'ijklmnopqrstuvwxyzabcdefgh',
    j: 'jklmnopqrstuvwxyzabcdefghi',
    k: 'klmnopqrstuvwxyzabcdefghij',
    l: 'lmnopqrstuvwxyzabcdefghijk',
    m: 'mnopqrstuvwxyzabcdefghijkl',
    n: 'nopqrstuvwxyzabcdefghijklm',
    o: 'opqrstuvwxyzabcdefghijklmn',
    p: 'pqrstuvwxyzabcdefghijklmno',
    q: 'qrstuvwxyzabcdefghijklmnop',
    r: 'rstuvwxyzabcdefghijklmnopq',
    s: 'stuvwxyzabcdefghijklmnopqr',
    t: 'tuvwxyzabcdefghijklmnopqrs',
    u: 'uvwxyzabcdefghijklmnopqrst',
    v: 'vwxyzabcdefghijklmnopqrstu',
    w: 'wxyzabcdefghijklmnopqrstuv',
    x: 'xyzabcdefghijklmnopqrstuvw',
    y: 'yzabcdefghijklmnopqrstuvwx',
    z: 'zabcdefghijklmnopqrstuvwxy',
  };

  loggedIn = signal(false);

  constructor() {
    this.environment = this.envSvc.get();

    if (!this.environment) {
      this.envSvc.loadSettings();
      this.environment = this.envSvc.get();
    }

    this.f5500BaseUrl = this.environment.f5500Base;
    this.baseUrl = this.environment.apiBase;
    this.org = this.environment.org;
    this.firm = this.environment.firm || '';
    this.app = this.ss.get('currentApp') || this.environment.defaultApp || 'rb';
    this.org = this.environment.org || 'basev2';
  }

  login(data): Observable<Login> {
    this.ss.clear();

    if (!this.baseUrl) {
      this.envSvc.loadSettings();
    }

    const reqHeaders = new HttpHeaders()
      .set('X-RB-U', data.name)
      .set('X-RB-P', data.pass)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);

    const url = this.baseUrl + '/Session/start';

    let result;
    let results;

    return this.http.get<Login>(url, { headers: reqHeaders }).pipe(
      map(res => {
        results = res;
        result = results.result;
        this.ss.set('session', result);
        const startPage = results.startPage
          ? results.startPage.toLowerCase()
          : null;
        this.app = startPage === 'unified' ? 'rb' : startPage;

        this.ss.set('startPage', startPage);
        this.ss.set('currentApp', startPage);

        return results;
      }),
      catchError((error: any) => {
        this.logout();
        return this.errSvc.handleError(error, false);
      })
    );
  }

  getToken(): Observable<{ access_token: string }> {
    if (this.token === null || this.isTokenExpired()) {
      const reqHeaders = new HttpHeaders()
        .set('Content-Type', 'application/json')
        .set('X-RB-O', this.getOrg())
        .set('X-RB-I', this.getApp());

      const url = this.baseUrl + '/Token';
      return this.http
        .post<{ access_token: string }>(
          url,
          `{"subjectToken":"${this.getSession()}"}`,
          {
            headers: reqHeaders,
          }
        )
        .pipe(
          map(res => {
            this.token = res['access_token'];
            this.ss.set('token', this.token);
            return res;
          })
        );
    } else {
      return of({ access_token: this.token });
    }
  }

  isTokenExpired(): boolean {
    const expiryTime: number = this.getExpiryTime(this.token);
    if (expiryTime) {
      return 1000 * expiryTime - new Date().getTime() < 5000;
    } else {
      return false;
    }
  }
  getExpiryTime(token): number | null {
    const decodedToken = this.decodeToken(token);
    return decodedToken ? decodedToken.exp : null;
  }

  decodeToken(jwtToken: string): { exp: number } | void {
    if (jwtToken) {
      return jwt_decode(jwtToken);
    }
  }

  getRlcPdf(filename: string): Observable<Blob | null> {
    const sess = this.sessionId;
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', this.app)
      .set('X-RB-O', this.org);

    const url = `${this.baseUrl}/Reports/RLC?filename=${filename}`;

    return this.http
      .get(url, { headers: reqHeaders, responseType: 'blob' })
      .pipe(
        map(res => {
          return res;
        }),
        catchError((error: any) => {
          return this.errSvc.handleError(error);
        })
      );
  }

  getFactsheet(filename: string): Observable<Blob | null> {
    const sess = this.sessionId;
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', 'pp')
      .set('X-RB-O', this.org);

    const url = `${this.baseUrl}/Reports/F5500FactSheet?planID=${filename}`;

    return this.http
      .get(url, { headers: reqHeaders, responseType: 'blob' })
      .pipe(
        map(res => {
          return res;
        }),
        catchError((error: any) => {
          return this.errSvc.handleError(error);
        })
      );
  }

  setParams(
    app: string = this.app,
    org: string = this.org,
    firm: string = this.firm
  ): void {
    this.ss.set('currentApp', app);
    this.firm = firm || this.setFirmId();
    this.app = app ? app.toLowerCase() : this.ss.get('currentApp');

    this.org = org;
  }

  setRogue(app = 'aw', org: string = this.org, session: string): void {
    this.ss.set('session', session);
    this.getGlobals().subscribe(data => {
      this.ss.set('rights', data.results.user.rights);
    });
    this.org = org;
    this.ss.set('hideMenu', true);
    this.firm = org;
  }

  setSso(
    org: string = this.org,
    session: string,
    app?: string,
    profile?: string,
    search?: string,
    bridgeId?: string,
    video?: string
  ): void {
    let rights = [];
    this.ss.set('session', session);
    this.getGlobals().subscribe(data => {
      this.ss.set('rights', data.results.user.rights);
      rights = data.results.user.rights;

      this.org = org;
      this.firm = this.setFirmId(data.results);

      const computedApp = app ? app : this.environment.defaultApp;
      let startPage = data.results.startPage
        ? data.results.startPage.toLowerCase()
        : computedApp.toLowerCase();

      if (!startPage) startPage = 'rb';
      this.ss.set('startPage', startPage === 'unified' ? 'rb' : startPage);

      const dashboard = rights.includes('NewDashboardTab');
      const appPage =
        computedApp &&
        (computedApp.toLowerCase() === 'aw' ||
          computedApp.toLowerCase() === 'iw' ||
          computedApp.toLowerCase() === 'li' ||
          computedApp.toLowerCase() === 'pp' ||
          computedApp.toLowerCase() === 'rb' ||
          computedApp.toLowerCase() === 'se');

      if (appPage) {
        this.app = computedApp.toLowerCase();
        this.ss.set('currentApp', computedApp.toLowerCase());
      }

      if (computedApp.toLowerCase() === 'se') {
        if (profile && this.app === 'se') {
          this.router.navigate(['/' + this.app + '/client/' + profile]);
        } else if (search && bridgeId && this.app === 'se') {
          this.router.navigate([
            '/' + this.app + '/search/' + search + '/' + bridgeId,
          ]);
        } else if (search && this.app === 'se') {
          this.router.navigate(['/' + this.app + '/search/' + search]);
        } else if (profile) {
          this.router.navigate(['/' + this.app + '/questionnaire/' + profile]);
        } else if (dashboard) {
          this.router.navigate(['/dashboard']);
        } else {
          this.router.navigate(['/' + this.app]);
        }
      } else {
        if (profile) {
          if (startPage === 'pp') {
            this.router.navigate([`case/rb/${profile}/0`]);
          } else if (computedApp.toLowerCase() === 'cm') {
            this.app = 'rb';
            this.ss.set('currentApp', this.app);
            this.router.navigate([`case-management/case-manager/${profile}`]);
          } else {
            this.router.navigate([`case/${this.app}/${profile}/0`]);
          }
        } else if (video) {
          this.router.navigate([`training/${this.app}/${video}`]);
        } else if (appPage) {
          this.router.navigate([`/unified`]);
        } else if (!appPage) {
          switch (computedApp.toLowerCase()) {
            case 'ps':
              this.router.navigate([`/aw/product-shelf`]);
              break;
            case 'fiar':
              this.router.navigate([`/aw/fia`]);
              break;
            case 'rr':
              this.router.navigate([`/aw/rila`]);
              break;
            case 'far':
              this.router.navigate([`/aw/fa`]);
              break;
            case 'pl':
              this.router.navigate([`/aw/lookup`]);
              break;
            case 'rc':
              this.router.navigate([`/aw/rmd-calculation`]);
              break;
            case 'fc':
              this.router.navigate([`/iw/lookup`]);
              break;
            case 'tm':
              this.router.navigate([`/iw/templates`]);
              break;
            case 'dash':
              this.router.navigate([`/dashboard`]);
              break;
            case 'tools':
              this.router.navigate([`/tools`]);
              break;
            case 'ue':
              this.router.navigate([`/unified`]);
              break;
            case 'cm':
              this.router.navigate([`/case-management`]);
              break;
            case 'iwps':
              this.router.navigate([`/iw/product-shelf`]);
              break;
            case 'bm':
            case 'pm':
            case 'book':
              this.router.navigate([`/book-management`]);
              break;
            default:
              this.router.navigate([`/unified`]);
              break;
          }
        } else if (dashboard) {
          this.router.navigate(['/dashboard']);
        } else {
          this.router.navigate(['/unified']);
        }
      }
    });
  }

  getSession(): string {
    return this.sessionId;
  }
  getApp(): string {
    return this.app;
  }
  getOrg(): string {
    return this.org;
  }
  getJwt(): string {
    return this.token;
  }
  clearJwt(): void {
    this.token = null;
    this.ss.set('token', this.token);
  }

  endSession(session?): Observable<void> {
    const sess = session || this.sessionId;

    const app = this.app || 'rb';

    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', app)
      .set('X-RB-O', this.org);

    const url = this.baseUrl + '/Session/end';

    return this.http.get<void>(url, { headers: reqHeaders }).pipe(
      map(res => {
        this.themSvc.setColorScheme('light');
        this.themSvc.setTheme('RightBridge');
        this.ss.clear();
        this.sessionId = null;
        return res;
      }),
      catchError((error: any) => {
        this.ss.clear();
        this.sessionId = null;
        return this.errSvc.handleError(error);
      })
    );
  }

  querySession(
    id?: string | null,
    org?: string | null,
    app?: string | null,
    sso = false
  ): Observable<ApiResponse<string | number> | null> {
    this.sessionId = id || this.sessionId;
    this.org = org || this.org;
    this.app = app ? app.toLowerCase() : this.ss.get('currentApp') || this.app;
    const requestApp = this.app === 'se' ? 'se' : 'rb';
    const session = id || this.sessionId || '';

    if (id) {
      this.ss.set('session', id);
    }
    if (app) {
      this.ss.set('currentApp', app);
    }
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', session)
      .set('X-RB-I', requestApp)
      .set('X-RB-O', this.org);

    const url = this.baseUrl + '/Session/query';

    if (this.sessionId) {
      return this.http
        .get<ApiResponse<string | number>>(url, { headers: reqHeaders })
        .pipe(
          map(res => {
            return res;
          }),
          catchError(() => {
            if (!sso) {
              this.logout();
            }
            return of({ results: 'invalid' });
          })
        );
    }
    return of(null);
  }

  resetSession(id?): Observable<void> {
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', id)
      .set('X-RB-I', this.app)
      .set('X-RB-O', this.org);

    const url = this.baseUrl + '/Session/reset';

    return this.http.get<void>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError((error: any) => {
        this.logout();
        return this.errSvc.handleError(error);
      })
    );
  }

  saveProfile(
    id,
    data,
    evaluate = false,
    returnProfile = false,
    passedApp?
  ): Observable<void> {
    const sessId = this.ss.get('session');
    let app;
    if (passedApp) {
      app = passedApp;
    } else if (this.app === 'summary') {
      app = 'pp';
    } else if (
      this.app === 'pp' &&
      (this.ss.get('currentStep') === 0 || this.ss.get('currentStep') === '0')
    ) {
      app = 'rb';
    } else {
      app = this.app;
    }
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', app)
      .set('X-RB-O', this.org)
      .set('Content-Type', 'application/json');
    const profile = id;

    let url = this.baseUrl + '/Profile/' + profile;

    url += `?evaluateFunctionsOnly=${evaluate}&`;

    if (!evaluate) {
      url += '?';
    }

    if (returnProfile) {
      url += 'returnProfile=true';
    } else {
      url += 'returnProfile=false';
    }
    return this.http.patch<void>(url, data, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError((error: any) => {
        this.logout();
        return this.errSvc.handleError(error);
      })
    );
  }

  newProfile(data, evaluate = false): Observable<ApiResponse<string>> {
    const sessId = this.ss.get('session');
    const app = this.app === 'rb' ? 'pp' : this.app;
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', app)
      .set('X-RB-O', this.org)
      .set('Content-Type', 'application/json');

    let url = this.baseUrl + '/Profile';

    if (evaluate) {
      url += '?evaluateFunctionsOnly=true';
    }

    return this.http
      .post<ApiResponse<string>>(url, data, { headers: reqHeaders })
      .pipe(
        map(res => {
          return res;
        }),
        catchError((error: any) => {
          this.logout();
          return this.errSvc.handleError(error);
        })
      );
  }

  copyProfile(id, desc): Observable<StringResult> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);
    const profile = id;

    const url =
      this.baseUrl + '/Profile/Copy/' + profile + '?profileDescription=' + desc;

    return this.http.get<StringResult>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError((error: any) => {
        this.logout();
        return this.errSvc.handleError(error);
      })
    );
  }

  swapProfile(id, desc): Observable<StringResult> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);
    const profile = id;

    const url =
      this.baseUrl + '/Profile/Swap/' + profile + '?profileDescription=' + desc;

    return this.http.get<StringResult>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError((error: any) => {
        this.logout();
        return this.errSvc.handleError(error);
      })
    );
  }

  deleteProfile(id): Observable<StringResult> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);
    const profile = id;

    const url = this.baseUrl + '/Profile/' + profile;

    return this.http.delete<StringResult>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError((error: any) => {
        this.logout();
        return this.errSvc.handleError(error);
      })
    );
  }

  getGlobals(
    session?: string | null,
    org?: string | null,
    app?: string | null,
    resolver: boolean | null = null
  ): Observable<ApiResponse<Global>> {
    let appName;

    if (
      this.ss.get('startPage') && this.ss.get('startPage').length > 0
        ? this.ss.get('startPage').toLowerCase() === 'se'
        : null
    ) {
      this.setApp('se');
    }

    if (this.app === 'se') {
      app = 'se';
    }

    if (
      (!app ||
        (this.app.toLowerCase() !== 'pp' &&
          this.app.toLowerCase() !== 'aw' &&
          this.app.toLowerCase() !== 'iw' &&
          this.app.toLowerCase() !== 'li' &&
          this.app.toLowerCase() !== 'se' &&
          this.app.toLowerCase() !== 'rb')) &&
      !resolver
    ) {
      this.setApp('rb');
    }

    if (app) {
      appName = app;
    } else {
      appName = this.app;
    }

    const id = session || this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', id)
      .set('X-RB-I', appName)
      .set('X-RB-O', this.org);

    const url = this.baseUrl + '/Globals';

    return this.http
      .get<ApiResponse<Global>>(url, { headers: reqHeaders })
      .pipe(
        map(res => {
          const data = res;
          this.ss.set('globals', data.results);

          if (
            !this.ss.get('repUnits') ||
            this.ss.get('repUnits').length === 0
          ) {
            this.getRepCodeList().subscribe(() => {
              this.firm = this.setFirmId();
              this.ss.set('firm', this.firm);
            });
          } else {
            this.firm = this.setFirmId();
            this.ss.set('firm', this.firm);
          }

          const rights = this.ss.get('globals').user.rights;
          this.ss.set('rights', rights);
          return data;
        }),
        catchError((error: any) => {
          this.logout();
          return this.errSvc.handleError(error);
        })
      );
  }

  getRepCodeList(): Observable<RepData[]> {
    let reqApp = this.app;
    if (['pp', 'aw', 'iw', 'li'].includes(this.app)) {
      reqApp = 'rb';
    } else {
      reqApp = this.app;
    }

    const session = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', session)
      .set('X-RB-I', reqApp)
      .set('X-RB-O', this.org);

    const url = `${this.baseUrl}/Hierarchy/RepCodeList`;

    return this.getRequest<any>(url, reqHeaders).pipe(
      map(res => {
        const data = res;
        this.ss.set('repUnits', data);

        return data;
      }),
      catchError((error: any) => {
        return this.errSvc.handleError(error);
      })
    );
  }

  getRepCodes(
    searchString: string,
    maxRows = 20
  ): Observable<{ names: RepData[] }> {
    let reqApp = this.app;
    if (['pp', 'aw', 'iw', 'li'].includes(this.app)) {
      reqApp = 'rb';
    } else {
      reqApp = this.app;
    }

    const session = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', session)
      .set('X-RB-I', reqApp)
      .set('X-RB-O', this.org);

    let url = `${this.baseUrl}/Hierarchy/RepCode?searchString=${searchString}&maxRows=${maxRows}`;

    if (inactiveUsersApps.includes(this.app)) {
      url = url + '&includeInactive=true';
    }

    return this.getRequest<any>(url, reqHeaders).pipe(
      map(res => {
        const data = res;
        return data;
      }),
      catchError((error: any) => {
        return this.errSvc.handleError(error);
      })
    );
  }

  getRights(): Observable<string[]> {
    let rights = null;
    rights = this.ss.get('rights');
    if (!rights) {
      return observableThrowError(() => 'No rights yet');
    }
    return of(rights);
  }

  getQuestions(
    profile?: string,
    app?: string,
    processing?: string
  ): Observable<ApiResponse<DynamicResponse<'questionnaire', Questionnaire>>> {
    const id = this.ss.get('session');

    if (app) {
      this.setApp(app);
    }

    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', id)
      .set('X-RB-I', this.app)
      .set('X-RB-O', this.org);
    let url = profile
      ? this.baseUrl + '/Questionnaire/' + profile
      : this.baseUrl + '/Questionnaire';

    if (this.app === 'rb') {
      url += '?type=universal-clientInfo';
    }

    if (processing) {
      url += `&processing=${processing}`;
    }

    return this.http
      .get<ApiResponse<DynamicResponse<'questionnaire', Questionnaire>>>(url, {
        headers: reqHeaders,
      })
      .pipe(
        map(res => {
          return res;
        }),
        catchError((error: any) => {
          return this.errSvc.handleError(error);
        })
      );
  }

  getQuestionsFromList(
    fields?: string[],
    profile?,
    app?
  ): Observable<QueryQuestionnaire> {
    const id = this.ss.get('session');

    if (app) {
      this.setApp(app);
    }

    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', id)
      .set('X-RB-I', this.app)
      .set('X-RB-O', this.org);
    const url = `${this.baseUrl}/Questionnaire/Query/${profile}`;

    return this.http
      .post<QueryQuestionnaire>(url, fields, { headers: reqHeaders })
      .pipe(
        map(res => {
          return res;
        }),
        catchError((error: any) => {
          return this.errSvc.handleError(error);
        })
      );
  }

  processFunds(
    program,
    asset,
    profile,
    shareClass?,
    fundFams?,
    type?
  ): Observable<{
    funds: {
      currFund: { MultiAssetClass: any };
      fundId: any;
      type: any;
      hidden: any;
    }[];
    limit: number;
  }> {
    const id = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', id)
      .set('X-RB-I', 'iw')
      .set('X-RB-O', this.org);
    this.firm = this.setFirmId(null, true) || this.firm;
    let url =
      this.baseUrl +
      '/Process/Funds/' +
      profile +
      '?programId=' +
      program +
      '&assetClass=' +
      asset +
      '&firmId=' +
      this.firm;

    if (shareClass) {
      url += '&shareClass=' + shareClass;
    }

    if (fundFams) {
      url += '&fundFamilyIds=' + fundFams;
    }

    if (type) {
      url += '&fundType=' + type;
    }

    return this.http
      .get<{
        funds: {
          currFund: { MultiAssetClass: any };
          fundId: any;
          type: any;
          hidden: any;
        }[];
        limit: number;
      }>(url, { headers: reqHeaders })
      .pipe(
        map(res => {
          return res;
        }),
        catchError((error: any) => {
          this.logout();
          return this.errSvc.handleError(error);
        })
      );
  }

  processAnnuities(profile: string): Observable<GroupProfile> {
    const id = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', id)
      .set('X-RB-I', 'aw')
      .set('X-RB-O', this.org);
    const url = this.baseUrl + '/Process/Annuities/' + profile;

    return this.http.get<GroupProfile>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError((error: any) => {
        this.logout();
        return this.errSvc.handleError(error);
      })
    );
  }

  processProducts(profile): Observable<ProcessProductsResponse> {
    const id = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', id)
      .set('X-RB-I', this.app)
      .set('X-RB-O', this.org);
    const url = this.baseUrl + '/Process/Products/' + profile;

    return this.http
      .get<ProcessProductsResponse>(url, { headers: reqHeaders })
      .pipe(
        map(res => {
          return res;
        }),
        catchError((error: any) => {
          this.logout();
          return this.errSvc.handleError(error);
        })
      );
  }

  selectFunds(id, data, clear?): Observable<void> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', this.app)
      .set('X-RB-O', this.org)
      .set('Content-Type', 'application/json');
    const profile = id;
    const fundData = { Values: [] };
    if (!clear) {
      fundData.Values = data;
    } else {
      fundData.Values[0] = {
        'SelectedFunds.FundID': '',
        'SelectedFunds.Symbol': '',
        'SelectedFunds.AssetClass': '',
        'SelectedFunds.Name': '',
        'SelectedFunds.Amount': '',
        'SelectedFunds.FundFamilyID': '',
        'SelectedFunds.IsSpecial': '',
        'SelectedFunds.FundType': '',
      };
    }
    const url = this.baseUrl + '/Select/Funds/' + profile;

    return this.http.patch<void>(url, fundData, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError((error: any) => {
        this.logout();
        return this.errSvc.handleError(error);
      })
    );
  }

  selectAnnuity(id, note, annuity, clearSelected): Observable<void> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', this.app)
      .set('X-RB-O', this.org)
      .set('Content-Type', 'application/json');
    const profile = id;

    const processedNote = note && note.length > 0 ? note : '';

    const axData = {
      'CurrentBridgeAction.ActiveFlag': 1,
      'CurrentBridgeAction.AcceptType': 2301,
      'CurrentBridgeAction.FWDateType': '',
      'CurrentBridgeAction.Fwdate': '',
      'CurrentBridgeAction.Note': processedNote,
      sysActions: 0,
      bridgeId: annuity,
      actionId: 2301,
    };

    let url = this.baseUrl + '/Select/Annuities/' + profile;

    if (clearSelected) {
      url += '?deselectAll=true';
    }

    return this.http.patch<void>(url, axData, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError((error: any) => {
        this.logout();
        return this.errSvc.handleError(error);
      })
    );
  }

  deselectAnnuity(id, note, annuity): Observable<void> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', this.app)
      .set('X-RB-O', this.org)
      .set('Content-Type', 'application/json');
    const profile = id;

    const axData = {
      'CurrentBridgeAction.ActiveFlag': 1,
      'CurrentBridgeAction.AcceptType': 2201,
      'CurrentBridgeAction.FWDateType': '',
      'CurrentBridgeAction.Fwdate': '',
      'CurrentBridgeAction.Note': 'Selection Removed',
      sysActions: 0,
      bridgeId: annuity,
      actionId: 2201,
    };

    const url = this.baseUrl + '/Select/Annuities/' + profile;

    return this.http.patch<void>(url, axData, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError((error: any) => {
        this.logout();
        return this.errSvc.handleError(error);
      })
    );
  }

  getReport(
    profile,
    details?,
    silent = false,
    vaDetails = false,
    validate = false
  ): Observable<Blob | null> {
    const id = this.ss.get('session');
    this.firm = this.firm && this.firm.length ? this.firm : this.setFirmId();
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', id)
      .set('X-RB-I', this.app)
      .set('X-RB-O', this.org)
      .set('Accept', 'application/pdf');
    const app = this.app === 'rb' || this.app === 'summary' ? 'pp' : this.app;

    let url =
      this.baseUrl + '/reports/' + app + '/' + profile + '?firmId=' + this.firm;

    if (
      (this.app.toLowerCase() === 'iw' || this.app.toLowerCase() === 'pp') &&
      details &&
      details === true
    ) {
      url += '&includeDetail=true';
    }

    if (this.app.toLowerCase() === 'aw') {
      url += '&selectedBridges=' + details;
    }

    if (vaDetails) {
      url += '&includeVAInvestmentOptions=true';
    }

    if (silent) {
      url += '&returnReport=false';
    }

    if (validate) {
      url += '&isValidation=true';
    }

    return this.http
      .post(url, null, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError((error: any) => {
          this.logout();
          return this.errSvc.handleError(error);
        })
      );
  }

  getRolloverReport(profile, details?): Observable<Blob | null> {
    const id = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', id)
      .set('X-RB-I', this.app)
      .set('X-RB-O', this.org)
      .set('Accept', 'application/pdf');

    const url = `${this.baseUrl}/reports/PPRollover/${profile}`;

    return this.http
      .post(url, null, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError((error: any) => {
          this.logout();
          return this.errSvc.handleError(error);
        })
      );
  }

  silentPrintPpReport(profile, details?): Observable<Blob | null> {
    const id = this.ss.get('session');
    this.firm = this.firm && this.firm.length ? this.firm : this.setFirmId();
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', id)
      .set('X-RB-I', 'pp')
      .set('X-RB-O', this.org)
      .set('Accept', 'application/pdf');

    const url =
      this.baseUrl +
      '/reports/pp/' +
      profile +
      '?firmId=' +
      this.firm +
      '&returnReport=false&includeDetail=true';

    return this.http
      .post(url, null, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError((error: any) => {
          this.logout();
          return this.errSvc.handleError(error);
        })
      );
  }

  getExchangeReport(profile, details?, silent?): Observable<Blob | null> {
    const id = this.ss.get('session');
    this.firm = this.firm && this.firm.length ? this.firm : this.setFirmId();
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', id)
      .set('X-RB-I', this.app)
      .set('X-RB-O', this.org)
      .set('Accept', 'application/pdf');
    let url =
      this.baseUrl + '/reports/AWExchange/' + profile + '?firmId=' + this.firm;

    url += '&selectedBridges=' + details;

    if (silent) {
      url += '&returnReport=false';
    }

    return this.http
      .post(url, null, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError((error: any) => {
          this.logout();
          return this.errSvc.handleError(error);
        })
      );
  }

  getClientReport(
    profiles,
    options,
    silentPrint = false
  ): Observable<Blob | null> {
    const id = this.ss.get('session');
    this.firm = this.firm && this.firm.length ? this.firm : this.setFirmId();
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', id)
      .set('X-RB-I', this.app)
      .set('X-RB-O', this.org)
      .set('Accept', 'application/pdf');
    const data = { profileIds: profiles };

    if (options.bridges && options.bridges.length) {
      data['bridgeIds'] = options.bridges;
      options.threshold = 0;
    }

    const returnReport = silentPrint ? false : true;

    const url = `${this.baseUrl}/reports/cp?returnReport=${returnReport}&minBridgeScore=${options.threshold}&includeAnnualReview=${options.annReview}&includeNeedSummary=${options.needSummary}&includeProductSummary=${options.prodSummary}&includeNeedDetail=${options.needDetail}&includeProductDetail=${options.prodDetail}`;

    return this.http
      .post(url, data, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError((error: any) => {
          this.logout();
          return this.errSvc.handleError(error);
        })
      );
  }

  getHistoricalFactSheet(data): Observable<Blob | null> {
    const id = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', id)
      .set('X-RB-I', 'aw')
      .set('X-RB-O', this.org)
      .set('Accept', 'application/pdf')
      .set('Content-Type', 'application/json');
    const url = this.baseUrl + '/Reports/HistoricalLookupFactSheet/';

    return this.http
      .post(url, data, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError((error: any) => {
          this.logout();
          return this.errSvc.handleError(error);
        })
      );
  }

  logout(): void {
    this.themSvc.setColorScheme('light');
    this.dialogRef.closeAll();
    this.endSession();
    this.ss.clear();
    this.zone.run(() => {
      this.router.navigate(['/login']);
    });
  }

  startProxy(user: { userID: string }): Observable<StringResult> {
    const userId = user.userID;
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', this.sessionId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org)
      .set('X-RB-U', userId);

    const url = this.baseUrl + '/Session/proxy';

    return this.http.get<StringResult>(url, { headers: reqHeaders }).pipe(
      map(res => {
        this.getGlobals(res['result']).subscribe();
        return res;
      }),
      catchError((error: any) => {
        return this.errSvc.handleError(error);
      })
    );
  }

  forgotPassword(data): Observable<void> {
    const userId = data;

    const reqHeaders = new HttpHeaders()
      .set('X-RB-I', this.app)
      .set('X-RB-O', this.org)
      .set('X-RB-U', userId);

    const url = this.baseUrl + '/Session/forgotpassword';

    return this.http.get<void>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError((error: any) => {
        return this.errSvc.handleError(error);
      })
    );
  }

  updatePassword(data): Observable<void> {
    const sess = this.ss.get('session');
    const globals = this.ss.get('globals');
    const userId = globals ? globals.user.id : null;
    const newPass = data.newPass;

    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', this.app)
      .set('X-RB-O', this.org)
      .set('X-RB-U', userId)
      .set('X-RB-P', newPass);

    const url = this.baseUrl + '/Admin/updatepassword';

    return this.http.get<void>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError((error: any) => {
        return this.errSvc.handleError(error);
      })
    );
  }

  postback(profile, app?): Observable<void> {
    const sess = this.sessionId;
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', app || this.app)
      .set('X-RB-O', this.org);

    const url = this.baseUrl + '/External/PostBack/' + profile;
    return this.http.get<void>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError((error: any) => {
        return this.errSvc.handleError(error);
      })
    );
  }

  extPostback(profile, solution?): Observable<PostbackResponse> {
    const sess = this.sessionId,
      customReportSolution = solution ? solution : this.app;

    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', customReportSolution)
      .set('X-RB-O', this.org);

    const url = this.baseUrl + '/External/PostBackURL/' + profile;
    return this.http.get<PostbackResponse>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError((error: any) => {
        return this.errSvc.handleError(error);
      })
    );
  }

  extPostbackData(profile, data, solution?): Observable<PostbackResponse> {
    const sess = this.sessionId;
    const reportSolution = solution ? solution : this.app;

    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', reportSolution)
      .set('X-RB-O', this.org);

    const url = this.baseUrl + '/External/PostBackURL/' + profile;
    return this.http
      .post<PostbackResponse>(url, data, { headers: reqHeaders })
      .pipe(
        map(res => {
          return res;
        }),
        catchError((error: any) => {
          return this.errSvc.handleError(error);
        })
      );
  }

  extStorage(data, profile, fileDataType?): Observable<void> {
    const id = this.ss.get('session');

    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', id)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);

    let url = `${this.baseUrl}/Profile/UploadFile/${profile}`;
    if (fileDataType) url += `?dataType=${fileDataType}`;

    return this.http.post<void>(url, data, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError((error: any) => {
        return this.errSvc.handleError(error);
      })
    );
  }

  getAnnuityCarriers(): Observable<AnnuityItem[]> {
    const sess = this.sessionId;
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', 'aw')
      .set('X-RB-O', this.org);
    const url = this.baseUrl + '/Annuities/Carriers';

    return this.http.get<AnnuityItem[]>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError((error: any) => {
        return this.errSvc.handleError(error);
      })
    );
  }

  getCarrierGroups(): Observable<{ Carriers: CarrierGroup[] }> {
    const sess = this.sessionId;
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', 'aw')
      .set('X-RB-O', this.org);
    const url = `${this.baseUrl}/Annuity/UnitCarriers`;

    return this.http
      .get<{ Carriers: CarrierGroup[] }>(url, { headers: reqHeaders })
      .pipe(
        map(res => {
          return res;
        }),
        catchError((error: any) => {
          return this.errSvc.handleError(error);
        })
      );
  }

  getRequest<T>(url, headers?, app?): Observable<T> {
    if (!headers) {
      const sess = this.sessionId;
      headers = new HttpHeaders()
        .set('X-RB-S', sess)
        .set('X-RB-I', app ? app : this.app)
        .set('X-RB-O', this.org);
    }
    return this.http.get<T>(url, { headers: headers }).pipe(
      map(res => {
        return res;
      }),
      catchError((error: any) => {
        return this.errSvc.handleError(error);
      })
    );
  }

  getRequestText(url, headers = null): Observable<string> {
    if (headers === null) {
      const sess = this.sessionId;
      headers = new HttpHeaders()
        .set('X-RB-S', sess)
        .set('X-RB-I', this.app)
        .set('X-RB-O', this.org);
    }
    return this.http.get(url, { headers: headers, responseType: 'text' }).pipe(
      map(res => {
        return res;
      }),
      catchError((error: any) => {
        return this.errSvc.handleError(error);
      })
    );
  }

  baseHeaders(): HttpHeaders {
    return new HttpHeaders()
      .set('X-RB-S', this.sessionId)
      .set('X-RB-I', this.app)
      .set('X-RB-O', this.org);
  }

  patchRequest<T>(url, body: any = null, headers?: HttpHeaders): Observable<T> {
    if (!headers) {
      headers = this.baseHeaders();
    }
    return this.http.patch<T>(url, body, { headers: headers }).pipe(
      map(res => {
        return res;
      }),
      catchError((error: any) => {
        return this.errSvc.handleError(error);
      })
    );
  }

  postRequest<T>(
    url,
    body: any = null,
    headers = null,
    response = null,
    app?
  ): Observable<T> {
    const requestApp = app ? app : this.app;

    if (headers === null) {
      const sess = this.sessionId;
      headers = new HttpHeaders()
        .set('X-RB-S', sess)
        .set('X-RB-I', requestApp)
        .set('X-RB-O', this.org);
    }

    return this.http.post<T>(url, body, { headers: headers }).pipe(
      map(res => {
        return res;
      }),
      catchError((error: any) => {
        return this.errSvc.handleError(error);
      })
    );
  }

  postRequestForFile(
    url,
    body = null,
    headers = null
  ): Observable<Blob | null> {
    if (headers === null) {
      const sess = this.sessionId;
      headers = new HttpHeaders()
        .set('X-RB-S', sess)
        .set('X-RB-I', this.app)
        .set('X-RB-O', this.org)
        .set('Accept', 'application/pdf')
        .set('Content-Type', 'application/json');
    }
    return this.http
      .post(url, body, {
        headers: headers,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError((error: any) => {
          return this.errSvc.handleError(error);
        })
      );
  }

  postRequestForCsv(
    url,
    body = null,
    headers = null
  ): Observable<string | Text> {
    if (headers === null) {
      const sess = this.sessionId;
      headers = new HttpHeaders()
        .set('X-RB-S', sess)
        .set('X-RB-I', this.app)
        .set('X-RB-O', this.org)
        .set('Accept', 'application/csv')
        .set('Content-Type', 'application/csv');
    }
    return this.http
      .post(url, body, {
        headers: headers,
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError((error: any) => {
          return this.errSvc.handleError(error);
        })
      );
  }

  deleteRequest<T>(url, headers = null, body = null): Observable<T> {
    if (headers === null) {
      const sess = this.sessionId;
      headers = new HttpHeaders()
        .set('X-RB-S', sess)
        .set('X-RB-I', this.app)
        .set('X-RB-O', this.org);
    }
    return this.http.delete<T>(url, { headers: headers, body: body }).pipe(
      map(res => {
        return res;
      }),
      catchError((error: any) => {
        return this.errSvc.handleError(error);
      })
    );
  }

  getGroupAnnuities(code): Observable<{ Annuities: AnnuityItem[] }> {
    const sess = this.sessionId;
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', 'aw')
      .set('X-RB-O', this.org);
    const url =
      this.baseUrl + '/Annuities/UnitProductsByCarrier?carrierGroupId=' + code;

    return this.http
      .get<{ Annuities: AnnuityItem[] }>(url, { headers: reqHeaders })
      .pipe(
        map(res => {
          return res;
        }),
        catchError((error: any) => {
          return this.errSvc.handleError(error);
        })
      );
  }

  getRegulationAnnuities(
    code: string | number,
    state?: string
  ): Observable<{ Annuities: AnnuityItem[] }> {
    const sess = this.sessionId;
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', 'aw')
      .set('X-RB-O', this.org);
    let url = `${this.baseUrl}/Annuity/UnitCarrierProducts?carrierGroupId=${code}`;

    if (state) {
      url += `&state=${state}`;
    }

    return this.http
      .get<{
        Annuities: { Annuities: AnnuityItem[] };
      }>(url, { headers: reqHeaders })
      .pipe(
        map(res => {
          return res.Annuities;
        }),
        catchError((error: any) => {
          return this.errSvc.handleError(error);
        })
      );
  }

  getClientSummary(profile): Observable<ClientSummary> {
    const sess = this.sessionId;
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', this.app)
      .set('X-RB-O', this.org);
    const url = `${this.baseUrl}/Process/ClientView/${profile}`;

    return this.http.get<ClientSummary>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError((error: any) => {
        return this.errSvc.handleError(error);
      })
    );
  }

  getUnifiedPrintParameters(id): Observable<GroupProfile> {
    return this.getGroupProfile(id, 'PrintSettings', 'pp', false);
  }

  getCustomPrintParameters(id, group): Observable<GroupProfile> {
    return this.getGroupProfile(id, group, 'pp', false);
  }

  getUnifiedReport(data, setApp?) {
    const sess = this.sessionId;

    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', 'pp')
      .set('X-RB-O', this.org);

    const url = `${this.baseUrl}/Reports/MultiReport`;

    return this.http
      .post(url, data, { headers: reqHeaders, responseType: 'blob' })
      .pipe(
        map(res => {
          return res;
        }),
        catchError((error: any) => {
          return this.errSvc.handleError(error);
        })
      );
  }

  getCustomReport(data, app, silentPrint = false, type = 'application/pdf') {
    const sess = this.sessionId;
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', app)
      .set('X-RB-O', this.org)
      .set('Accept', type);

    let url = `${this.baseUrl}/Reports/Custom`;

    if (silentPrint) {
      url += '?returnReport=false';
    }

    return this.http
      .post(url, data, { headers: reqHeaders, responseType: 'blob' })
      .pipe(
        map(res => {
          return res;
        }),
        catchError((error: any) => {
          return this.errSvc.handleError(error);
        })
      );
  }

  getNotes(profile): Observable<Notes> {
    const sess = this.sessionId;
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', this.app)
      .set('X-RB-O', this.org);
    const url = `${this.baseUrl}/Notes/${profile}`;

    return this.http.get<Notes>(url, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError((error: any) => {
        return this.errSvc.handleError(error);
      })
    );
  }

  addNote(profile, note): Observable<void> {
    const sess = this.sessionId;
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', this.app)
      .set('X-RB-O', this.org);
    const url = `${this.baseUrl}/Notes/Add/${profile}?noteType=0&noteText=${note.text}&isPinned=${note.pinned}&isAlert=${note.alert}`;

    return this.http.post<void>(url, null, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError((error: any) => {
        return this.errSvc.handleError(error);
      })
    );
  }

  getPershingGroupProfile(profile): Observable<GroupProfile> {
    return this.getGroupProfile(profile, 'PershingModal', 'pp', false);
  }

  pinToggle(profile, noteId, pinStatus): Observable<void> {
    const sess = this.sessionId;
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', this.app)
      .set('X-RB-O', this.org);
    const url = `${this.baseUrl}/Notes/Pin/${profile}/${noteId}?isPinned=${pinStatus}`;

    return this.http.patch<void>(url, null, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError((error: any) => {
        return this.errSvc.handleError(error);
      })
    );
  }

  signLicenseAgreement(sig) {
    const sess = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sess)
      .set('X-RB-I', this.app)
      .set('X-RB-O', this.org);
    const url = `${this.baseUrl}/User/LicenseAttest?Signature=${sig}`;
    return this.http.post(url, null, { headers: reqHeaders }).pipe(
      map(res => {
        return res;
      }),
      catchError((error: any) => {
        return this.errSvc.handleError(error);
      })
    );
  }

  setFirmId(glbls?, useGlobalFirm = false) {
    const repUnits = this.ss.get('repUnits') || null;
    const firm = glbls ? glbls.firmId : this.ss.get('globals')?.firmId;

    if (repUnits && repUnits.length === 1 && !useGlobalFirm) {
      return repUnits[0].id;
    }
    return firm;
  }

  setApp(app) {
    if (app === 'summary') {
      this.app = 'rb';
    } else {
      this.app = app;
    }
    this.ss.set('currentApp', app);
  }

  authenticate(): Observable<string> {
    const sess = this.ss.get('session'),
      globals = this.ss.get('globals'),
      userId = globals ? globals.user.id : null;

    const encUser = this.encodeEmail(userId);

    const reqHeaders = new HttpHeaders()
      .set('X-RB-I', this.app)
      .set('X-RB-O', this.org)
      .set('X-RB-U', encUser + '@rightbridge.net')
      .set('X-RB-S', sess)
      .set('Accept', 'text/html')
      .set('Content-Type', 'application/x-www-form-urlencoded');
    const body = '{"attributes":[{"firmId":"cr"},{"unitId":"CNO"}]}';

    const url =
      this.baseUrl +
      '/Session/createusersamlresponse?entityId=CapitalRock&recipient=recipient&audience=audience';

    return this.http
      .post(url, body, { headers: reqHeaders, responseType: 'text' })
      .pipe(
        map(res => {
          return res;
        }),
        catchError((error: any) => {
          return observableThrowError(
            () => error.error || 'Server error: ',
            error
          );
        })
      );
  }

  encodeEmail(value, isDecrypt?) {
    if (isDecrypt) {
      for (let i = 0; i < this.key.length; i++)
        this.key[i] = (26 - this.key[i]) % 26;
    }
    return this.encrypt(value, this.key);
  }

  encrypt(plainText, keyword) {
    if (typeof plainText !== 'string') {
      return 'invalid plainText. Must be string, not ' + typeof plainText;
    }
    if (typeof keyword !== 'string') {
      return 'invalid keyword. Must be string, not ' + typeof keyword;
    }

    plainText = plainText.toLowerCase();
    keyword = keyword.match(/[a-z]/gi).join('').toLowerCase();
    let encryptedText = '';
    let specialCharacterCount = 0;

    for (let i = 0; i < plainText.length; i++) {
      const keyLetter = (i - specialCharacterCount) % keyword.length;
      const keywordIndex = this.tabulaRecta.a.indexOf(keyword[keyLetter]);

      if (this.tabulaRecta[plainText[i]]) {
        encryptedText += this.tabulaRecta[plainText[i]][keywordIndex];
      } else {
        encryptedText += plainText[i];
        specialCharacterCount++;
      }
    }

    return encryptedText;
  }

  getGroupProfile(
    profile: string,
    groupName = profile,
    solution = 'rb',
    save = false,
    empty = false,
    omitSaveEmpty = false
  ): Observable<GroupProfile> {
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', this.getSession())
      .set('X-RB-I', solution)
      .set('X-RB-O', this.org);
    let url = `${this.baseUrl}/GroupProfile/${profile}?groupName=${groupName}`;

    if (!omitSaveEmpty) {
      url += `&saveProfile=${save}&isEmptyProfile=${empty}`;
    }
    return this.getRequest<GroupProfile>(url, reqHeaders);
  }

  downloadGroupProfile(
    id: string,
    solution: string,
    group: string,
    save: boolean
  ): Observable<Blob | null> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', solution)
      .set('X-RB-O', this.org)
      .set('accept', 'application/json');

    const url = `${this.baseUrl}/GroupProfile/${id}?groupName=${group}&saveProfile=${save}`;

    return this.http
      .get(url, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError((error: any) => {
          return this.errSvc.handleError(error);
        })
      );
  }

  postGroupProfile(
    group,
    app?,
    data?,
    save = false,
    empty = false
  ): Observable<GroupProfile> {
    return this.postRequest<GroupProfile>(
      `${this.baseUrl}/GroupProfile?groupName=${group}&saveProfile=${save}&isEmptyProfile=${empty}`,
      data,
      null,
      null,
      app
    );
  }

  patchGroupProfile(data, group, profile, app = 'rb', save = true) {
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', this.sessionId)
      .set('X-RB-I', app)
      .set('X-RB-O', this.org);
    let url = `${this.baseUrl}/GroupProfile?groupName=${group}`;
    url += `&saveProfile=${save}`;
    if (profile) {
      url += `&profileId=${profile}`;
    }

    return this.patchRequest<GroupProfile>(url, data, reqHeaders);
  }

  selectiveCopyProfile(reqBody): Observable<{ results: Unified }> {
    return this.postRequest<{ results: Unified }>(
      `${this.baseUrl}/Profile/ProfileCopy`,
      reqBody,
      null,
      null,
      'rb'
    );
  }

  getToolsPageGroup(): Observable<GroupProfile> {
    return this.postGroupProfile('Tools', 'rb');
  }

  getLandingPageGroup(): Observable<GroupProfile> {
    return this.postGroupProfile('LandingPage', 'rb');
  }

  getf5500Products(token, params, rlc?): Observable<ApiResponse<GridResults>> {
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', this.getSession())
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org)
      .set('Authorization', `Bearer ${token}`);

    let url = `${this.f5500BaseUrl}/products/product2020?Action=SEARCHPLANSAPI&RowsToReturn=200&grid=true`;
    url += rlc ? `&HasRLCData=true&${params}` : `&${params}`;

    return this.getRequest<ApiResponse<GridResults>>(url, reqHeaders);
  }

  getAISummary(body: {
    name: string;
    UIConfig: string;
    profileID: string;
    preprompt: string;
    format?: string;
  }): Observable<string> {
    const id = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', id)
      .set('X-RB-I', 'pp')
      .set('X-RB-O', this.org);
    const url = this.baseUrl + `/Widget/AiProcess`;

    return this.http
      .post(url, body, { headers: reqHeaders, responseType: 'text' })
      .pipe(
        map(res => {
          return res;
        }),
        catchError(error => {
          console.error(error);
          return this.errSvc.handleError(error);
        })
      );
  }

  uploadFileToProcess(
    data: FormData,
    promptName?: string,
    ocrType?: string,
    context?: string,
    excludeAI?: boolean
  ): Observable<string | null> {
    const id = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', id)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org)
      .set('Accept', 'text/plain');

    // Include only truthy options; HttpParams handles delimiters and encoding.
    let params = new HttpParams();
    if (promptName) {
      params = params.set('promptName', promptName);
    }
    if (ocrType) {
      params = params.set('ocrType', ocrType);
    }
    if (context) {
      params = params.set('context', context);
    }
    if (excludeAI) {
      params = params.set('excludeAI', excludeAI);
    }
    const query = params.toString();
    const url = `${this.baseUrl}/AI/ProcessDocument${query ? `?${query}` : ''}`;

    // return of(`{"test": "test"}`);
    return this.http
      .post(url, data, {
        headers: reqHeaders,
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        map(res => {
          return res.body;
        }),
        catchError((error: any) => {
          return this.errSvc.handleError(error);
        })
      );
  }

  decryptValue(value): Observable<ResultString> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', 'rb')
      .set('X-RB-O', this.org);
    const url = this.baseUrl + `/Admin/PlainFormat`;
    const data = {
      targetString: value,
    };
    return this.http
      .post<ResultString>(url, data, { headers: reqHeaders })
      .pipe(
        map(res => {
          return res;
        }),
        catchError(error => {
          return this.errSvc.handleError(error);
        })
      );
  }
}
